import { useEffect, type ReactNode } from 'react';
import { markAppBooted } from '@/components/RouteFallback/bootState';

/**
 * Dismisses the static index.html preboot once the first lazy route
 * has committed. Living inside <Suspense> means useEffect only fires
 * after the route resolves — which is exactly when we want the
 * handoff to start.
 *
 * Sequence (550ms total):
 *   t=0     useEffect fires; rAF defers one frame so the route paints
 *           cleanly before any preboot motion begins
 *   t=1f    add .preboot--handoff
 *           → wordmark fades out (0–175ms)
 *           → brand mark glides to viewport center (0–350ms)
 *   t=350ms add .preboot--hide
 *           → preboot fades out (350–550ms)
 *   t=550ms animationend → preboot.remove()
 *
 * Why two phases? A single crossfade would mean the wordmark and the
 * mark are moving and fading at the same time — reads as chaos. The
 * wordmark leaves first so the eye locks onto the mark before motion
 * starts; the mark then moves alone, which reads as intentional.
 *
 * Why rAF before adding the class? useEffect runs after React commits,
 * but commit happens BEFORE the browser paints. Adding the class
 * synchronously means the fade and the route's first paint composite
 * in the same frame, which can cause a visible "double dim" on slower
 * devices. rAF defers by one frame so the route paints first, then
 * the preboot starts animating over the now-visible route.
 *
 * Why animationend instead of setTimeout(550)? Decouples JS from CSS.
 * Change the CSS duration and animationend stays correct; setTimeout
 * would silently cut off or leave the preboot lingering.
 */
const HANDOFF_DURATION_MS = 350;
const FADE_KEYFRAME_NAME = 'prebootFadeOut';

export default function PrebootDismisser({
  children
}: {
  children: ReactNode;
}) {
  useEffect(() => {
    // The first route is on screen: later Suspense falls back to a skeleton
    // rather than replaying the splash.
    markAppBooted();

    const preboot = document.getElementById('preboot');
    if (!preboot) return;

    let handoffTimer: number | undefined;
    let onAnimationEnd: ((e: AnimationEvent) => void) | undefined;

    const raf = requestAnimationFrame(() => {
      preboot.classList.add('preboot--handoff');

      handoffTimer = window.setTimeout(() => {
        preboot.classList.add('preboot--hide');
      }, HANDOFF_DURATION_MS);

      // Multiple animations fire animationend events (wordmark, mark,
      // and finally the fade). Only remove on the fade event —
      // otherwise the preboot would vanish mid-handoff.
      onAnimationEnd = (e) => {
        if (e.animationName === FADE_KEYFRAME_NAME) {
          preboot.remove();
        }
      };
      preboot.addEventListener('animationend', onAnimationEnd);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      cancelAnimationFrame(raf);
      if (handoffTimer !== undefined) window.clearTimeout(handoffTimer);
      if (onAnimationEnd) {
        preboot.removeEventListener('animationend', onAnimationEnd);
      }
    };
  }, []);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}
